import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Check, X, FileText } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';

const BatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canApprovePayments } = useAuth();
  
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // For selective rejection
  const [rejectingPaymentId, setRejectingPaymentId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // For confirmation modal
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: '',
    confirmStyle: 'btn-primary',
    onConfirm: null,
  });

  useEffect(() => {
    fetchBatch();
  }, [id]);

  const fetchBatch = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/batches/${id}/`);
      setBatch(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load batch');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Modal helper functions
  const openConfirmModal = ({ title, message, confirmText, confirmStyle = 'btn-primary', onConfirm }) => {
    setConfirmModal({
      show: true,
      title,
      message,
      confirmText,
      confirmStyle,
      onConfirm,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      show: false,
      title: '',
      message: '',
      confirmText: '',
      confirmStyle: 'btn-primary',
      onConfirm: null,
    });
  };

  const handleConfirmAction = async () => {
    if (confirmModal.onConfirm) {
      await confirmModal.onConfirm();
    }
    closeConfirmModal();
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      DRAFT: { backgroundColor: '#6b7280', color: 'white' },
      PENDING_APPROVAL: { backgroundColor: '#f59e0b', color: 'white' },
      APPROVED: { backgroundColor: '#10b981', color: 'white' },
      PROCESSING: { backgroundColor: '#3b82f6', color: 'white' },
      COMPLETED: { backgroundColor: '#6366f1', color: 'white' },
      PENDING: { backgroundColor: '#6b7280', color: 'white' },
      REJECTED: { backgroundColor: '#ef4444', color: 'white' },
      PAID: { backgroundColor: '#10b981', color: 'white' },
    };

    const statusLabels = {
      DRAFT: 'Draft',
      PENDING_APPROVAL: 'Pending Approval',
      APPROVED: 'Approved',
      PROCESSING: 'Processing',
      COMPLETED: 'Completed',
      PENDING: 'Pending',
      REJECTED: 'Rejected',
      PAID: 'Paid',
    };

    return (
      <span
        style={{
          ...statusStyles[status],
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
        }}
      >
        {statusLabels[status]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return parseFloat(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 });
  };

  // Render the total amount based on single vs mixed currencies
  const renderTotalAmount = () => {
    const ct = batch?.currency_totals;
    
    // No currency_totals data (fallback)
    if (!ct) {
      return (
        <div className="currency-breakdown-inline">
          <div className="breakdown-row">
            <span className="breakdown-currency-tag">ZAR</span>
            <span>{formatAmount(batch.total_amount)}</span>
          </div>
        </div>
      );
    }
    
    // No payments
    if (!ct.totals || ct.totals.length === 0) {
      return '-';
    }
    
    // Always show with currency badge (single or multiple)
    return (
      <div className="currency-breakdown-inline">
        {ct.totals.map((item, index) => (
          <div key={index} className="breakdown-row">
            <span className="breakdown-currency-tag">{item.currency}</span>
            <span>{formatAmount(item.total)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Approve all pending payments
  const handleApproveAll = () => {
    const pendingCount = batch.payments.filter(p => p.status === 'PENDING').length;
    
    openConfirmModal({
      title: 'Approve All Payments',
      message: `Are you sure you want to approve all ${pendingCount} pending payment${pendingCount !== 1 ? 's' : ''} in this batch?`,
      confirmText: 'Approve All',
      confirmStyle: 'btn-success',
      onConfirm: async () => {
        setActionLoading(true);
        setError(null);
        
        try {
          await api.post(`/api/batches/${id}/approve_all/`);
          await fetchBatch();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to approve batch');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  // Approve single payment
  const handleApprovePayment = async (paymentId) => {
    setActionLoading(true);
    setError(null);
    try {
      await api.post('/api/payments/approve/', { payment_ids: [paymentId] });
      // Update state locally instead of full refetch
      setBatch(prev => ({
        ...prev,
        payments: prev.payments.map(p =>
          p.id === paymentId ? { ...p, status: 'APPROVED' } : p
        )
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Reject single payment
  const handleRejectPayment = async (paymentId) => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      await api.post('/api/payments/reject/', {
        payment_ids: [paymentId],
        rejection_reason: rejectionReason,
      });
      setRejectingPaymentId(null);
      setRejectionReason('');
      setBatch(prev => ({
        ...prev,
        payments: prev.payments.map(p =>
          p.id === paymentId ? { ...p, status: 'REJECTED', rejection_reason: rejectionReason } : p
        )
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Finish review
  const handleFinishReview = () => {
    const pendingCount = batch.payments.filter(p => p.status === 'PENDING').length;
    const approvedCount = batch.payments.filter(p => p.status === 'APPROVED').length;
    
    // Check if there are pending payments - show warning modal
    if (pendingCount > 0) {
      openConfirmModal({
        title: 'Cannot Finish Review',
        message: (
          <div>
            <p style={{ marginBottom: '12px' }}>
              You have <strong>{pendingCount} payment{pendingCount !== 1 ? 's' : ''}</strong> still pending review.
            </p>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              Please approve or reject all payments before finishing the review.
            </p>
          </div>
        ),
        confirmText: 'OK',
        confirmStyle: 'btn-primary',
        onConfirm: () => {}, // Just close the modal
      });
      return;
    }
    
    // Check if there are any approved payments
    if (approvedCount === 0) {
      openConfirmModal({
        title: 'Cannot Finish Review',
        message: 'There are no approved payments in this batch. Please approve at least one payment to proceed.',
        confirmText: 'OK',
        confirmStyle: 'btn-primary',
        onConfirm: () => {}, // Just close the modal
      });
      return;
    }
    
    // All good - confirm finish review
    openConfirmModal({
      title: 'Finish Review',
      message: `All ${approvedCount} payment${approvedCount !== 1 ? 's have' : ' has'} been approved. Are you sure you want to complete the review and proceed?`,
      confirmText: 'Finish Review',
      confirmStyle: 'btn-primary',
      onConfirm: executeFinishReview,
    });
  };

  const executeFinishReview = async () => {
    setActionLoading(true);
    setError(null);
    
    try {
      await api.post(`/api/batches/${id}/finish_review/`);
      await fetchBatch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finish review');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading batch...</div>;
  if (error && !batch) return <div className="error">{error}</div>;
  if (!batch) return <div className="error">Batch not found</div>;

  const pendingPayments = batch.payments.filter(p => p.status === 'PENDING');
  const approvedPayments = batch.payments.filter(p => p.status === 'APPROVED');
  const rejectedPayments = batch.payments.filter(p => p.status === 'REJECTED');
  const isPendingApproval = batch.status === 'PENDING_APPROVAL';
  const canApprove = canApprovePayments();

  return (
    <div className="batch-detail-container">
      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        loading={actionLoading}
        onConfirm={handleConfirmAction}
        onCancel={closeConfirmModal}
      />

      {/* Header */}
      <div className="batch-detail-header">
        <button className="btn btn-secondary" onClick={() => navigate('/batches')}>
          <ArrowLeft size={18} />
          Back to Batches
        </button>
        <div className="header-title">
            <h1>{batch.batch_reference}</h1>
            {getStatusBadge(batch.status)}
            {batch.status === 'DRAFT' && (
                <button 
                className="btn btn-primary"
                onClick={() => navigate(`/batches/${id}/edit`)}
                >
                    Edit Batch
                </button>
            )}
        </div>
      </div>

      {/* Error message */}
      {error && <div className="error-banner">{error}</div>}

      {/* Batch Info Card */}
      <div className="card">
        <h2>Batch Details</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Description</label>
            <span>{batch.description || '-'}</span>
          </div>
          <div className="info-item">
            <label>Payment Date</label>
            <span>{formatDate(batch.payment_date)}</span>
          </div>
          <div className="info-item">
            <label>Created By</label>
            <span>{batch.created_by_name}</span>
          </div>
          <div className="info-item">
            <label>Created At</label>
            <span>{formatDateTime(batch.created_at)}</span>
          </div>
          {batch.submitted_at && (
            <>
              <div className="info-item">
                <label>Submitted By</label>
                <span>{batch.submitted_by_name}</span>
              </div>
              <div className="info-item">
                <label>Submitted At</label>
                <span>{formatDateTime(batch.submitted_at)}</span>
              </div>
            </>
          )}
          {batch.reviewed_at && (
            <>
              <div className="info-item">
                <label>Reviewed By</label>
                <span>{batch.reviewed_by_name}</span>
              </div>
              <div className="info-item">
                <label>Reviewed At</label>
                <span>{formatDateTime(batch.reviewed_at)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <span className="summary-value">{batch.payment_count}</span>
          <span className="summary-label">Total Payments</span>
        </div>
        <div className="summary-card approved">
          <span className="summary-value">{approvedPayments.length}</span>
          <span className="summary-label">Approved</span>
        </div>
        <div className="summary-card pending">
          <span className="summary-value">{pendingPayments.length}</span>
          <span className="summary-label">Pending</span>
        </div>
        <div className="summary-card rejected">
          <span className="summary-value">{rejectedPayments.length}</span>
          <span className="summary-label">Rejected</span>
        </div>
        <div className="summary-card total">
          <span className="summary-value">{renderTotalAmount()}</span>
          <span className="summary-label">Total Amount</span>
        </div>
      </div>

      {/* Approval Actions - Only show if pending approval AND user can approve */}
      {isPendingApproval && canApprove && (
        <div className="approval-actions card">
          <h2>Approval Actions</h2>
          <div className="action-buttons">
            <button
              className="btn btn-success"
              onClick={handleApproveAll}
              disabled={actionLoading || pendingPayments.length === 0}
            >
              <CheckCircle size={18} />
              Approve All ({pendingPayments.length})
            </button>
            <button
                className="btn btn-primary"
                onClick={handleFinishReview}
                disabled={actionLoading}
            >
                <Check size={18} />
                Finish Review
            </button>
          </div>
          <p className="action-hint">
            {pendingPayments.length > 0 
                ? `Approve or reject all payments to finish review (${pendingPayments.length} pending)`
                : 'All payments decided. Ready to finish review.'
            }
          </p>
        </div>
      )}

      {/* Payments List */}
      <div className="card">
        <h2>Payments</h2>
        
        {batch.payments.length === 0 ? (
          <div className="empty-state">No payments in this batch</div>
        ) : (
          <table className="payments-detail-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Currency</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Invoice #</th>
                <th>Invoice</th>
                <th>Status</th>
                {isPendingApproval && canApprove && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {batch.payments.map((payment) => (
                <React.Fragment key={payment.id}>
                  <tr className={`payment-row status-${payment.status.toLowerCase()}`}>
                    <td>
                      <div className="supplier-info">
                        <span className="supplier-name">{payment.supplier_name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="breakdown-currency-tag">{payment.currency}</span>
                    </td>
                    <td className="amount">
                      {formatAmount(payment.amount)}
                    </td>
                    <td>{payment.reference}</td>
                    <td>{payment.invoice_number || '-'}</td>
                    <td className="invoice-cell">
                        {payment.invoice ? (
                            <a 
                            href={payment.invoice}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="invoice-link"
                            title="View invoice"
                            >
                            <FileText size={14} />
                            View
                            </a>
                        ) : (
                            <span className="no-invoice">None</span>
                        )}
                    </td>
                    <td>{getStatusBadge(payment.status)}</td>
                    {isPendingApproval && canApprove && (
                        <td className="actions-cell">
                            {payment.status === 'PENDING' ? (
                            <>
                                <button
                                className="btn-icon approve"
                                onClick={() => handleApprovePayment(payment.id)}
                                disabled={actionLoading}
                                title="Approve"
                                >
                                <Check size={16} />
                                </button>
                                <button
                                className="btn-icon reject"
                                onClick={() => setRejectingPaymentId(payment.id)}
                                disabled={actionLoading}
                                title="Reject"
                                >
                                <X size={16} />
                                </button>
                            </>
                            ) : (
                            <span className="action-placeholder">—</span>
                            )}
                        </td>
                    )}
                  </tr>
                  
                  {/* Rejection reason input row */}
                  {rejectingPaymentId === payment.id && (
                    <tr className="rejection-row">
                      <td colSpan={isPendingApproval && canApprove ? "8" : "7"}>
                        <div className="rejection-input">
                          <input
                            type="text"
                            placeholder="Enter rejection reason..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            autoFocus
                          />
                          <button
                            className="btn btn-danger btn-small"
                            onClick={() => handleRejectPayment(payment.id)}
                            disabled={actionLoading}
                          >
                            Confirm Reject
                          </button>
                          <button
                            className="btn btn-secondary btn-small"
                            onClick={() => {
                              setRejectingPaymentId(null);
                              setRejectionReason('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Show rejection reason if rejected */}
                {payment.status === 'REJECTED' && payment.rejection_reason && (
                <tr className="rejection-reason-row">
                    <td colSpan={isPendingApproval && canApprove ? "8" : "7"}>
                    <div className="rejection-reason">
                        <XCircle size={14} />
                        <span>Rejection reason: {payment.rejection_reason}</span>
                    </div>
                    </td>
                </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BatchDetail;